export interface VideoRendererHost {
  // First decoded frame: a socket opening is not proof the pipeline works.
  onStarted(): void;
  // Unrecoverable for this renderer. `terminal` means no other transport can
  // help either, because the failure is upstream of the transport.
  onFatal(reason: string, terminal?: boolean): void;
}

export interface VideoRenderer {
  start(): void;
  destroy(): void;
  play(): void;
  pause(): void;
  // Read by the player's stall watchdog.
  lastFrameAt(): number;
}

// Base for the renderers that draw into a <video>. jsmpeg owns its own canvas.
export abstract class VideoElementRenderer implements VideoRenderer {
  private started: boolean = false;
  private lastFrame: number = 0;
  private readonly onProgress = (): void => {
    this.lastFrame = Date.now();
    if (!this.started) {
      this.started = true;
      this.host.onStarted();
    }
  };

  protected constructor(
    protected readonly video: HTMLVideoElement,
    protected readonly host: VideoRendererHost
  ) {}

  public abstract start(): void;

  public destroy(): void {
    this.video.removeEventListener('loadeddata', this.onProgress);
    this.video.removeEventListener('timeupdate', this.onProgress);
    try {
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.srcObject = null;
      this.video.load();
    } catch {
      // Already detached.
    }
  }

  // The stall window restarts here: the last frame may date from before a pause.
  public play(): void {
    this.lastFrame = Date.now();
    this.video.play().catch(() => { /* autoplay rejected; the user can press play */ });
  }

  public pause(): void {
    this.video.pause();
  }

  public lastFrameAt(): number {
    return this.lastFrame;
  }

  // loadeddata is the first frame, timeupdate the heartbeat afterwards.
  protected observeVideo(): void {
    this.video.addEventListener('loadeddata', this.onProgress);
    this.video.addEventListener('timeupdate', this.onProgress);
  }
}
